import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Input,
  Label,
  Switch,
} from "@my-saas/ui";
import { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useParams } from "react-router";

import { toast } from "@/client/hooks/use-toast";
import {
  useAlertMessages,
  useCardMappings,
  useCardMappingMutations,
  useLocationGroups,
} from "@/client/services/tenant-config";

/**
 * OREF card mappings — auto-discovered rows that link incoming OREF titles to
 * an AlertMessage. The engine auto-creates a disabled mapping the first time
 * it sees a new title; the operator wires it to a card here.
 */
export const CardMappingsPage = () => {
  const { tenantSlug } = useParams<{ tenantSlug: string }>();
  const { data: mappings, isPending } = useCardMappings(tenantSlug);
  const { data: messages } = useAlertMessages(tenantSlug);
  const { data: groups } = useLocationGroups(tenantSlug);
  const { update, setAssignments } = useCardMappingMutations(tenantSlug);

  return (
    <>
      <Helmet>
        <title>Card mappings · {tenantSlug}</title>
      </Helmet>

      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-semibold">OREF card mappings</h1>
          <p className="text-sm text-muted-foreground">
            Each OREF alert title (Hebrew) auto-creates a disabled mapping.
            Pick the AlertMessage to send + the location scope (groups or
            specific cities, optional — defaults to all subscribed locations).
          </p>
        </div>

        {isPending && <p className="text-sm text-muted-foreground">Loading…</p>}

        {mappings?.length === 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="text-base">No mappings yet</CardTitle>
              <CardDescription>
                Mappings appear automatically as the engine processes new OREF
                titles. Until then, no alerts will fire WhatsApp dispatches.
              </CardDescription>
            </CardHeader>
          </Card>
        )}

        {mappings?.map((m) => (
          <CardMappingRow
            key={m.id}
            mapping={m}
            messages={messages ?? []}
            groups={groups ?? []}
            onPatch={(patch) => update.mutate({ id: m.id, ...patch })}
            onSetAssignments={(groupIds, cityValues) => {
              void setAssignments.mutateAsync({ id: m.id, groupIds, cityValues }).then(() => {
                toast({ title: "Assignments saved" });
              });
            }}
          />
        ))}
      </div>
    </>
  );
};

interface RowProps {
  mapping: {
    id: string;
    orefTitle: string;
    cardId: string;
    enabled: boolean;
    sendHe: boolean;
    sendEn: boolean;
    locationAssignments: { type: "group" | "city"; refId: string }[];
  };
  messages: { id: string; name: string }[];
  groups: { id: string; name: string; status: string }[];
  onPatch: (patch: Partial<{ cardId: string; enabled: boolean; sendHe: boolean; sendEn: boolean }>) => void;
  onSetAssignments: (groupIds: string[], cityValues: string[]) => void;
}

function CardMappingRow({ mapping, messages, groups, onPatch, onSetAssignments }: RowProps) {
  const initialGroupIds = mapping.locationAssignments
    .filter((a) => a.type === "group")
    .map((a) => a.refId);
  const initialCityValues = mapping.locationAssignments
    .filter((a) => a.type === "city")
    .map((a) => a.refId);

  const [selectedGroupIds, setSelectedGroupIds] = useState<string[]>(initialGroupIds);
  const [cardId, setCardId] = useState(mapping.cardId);

  useEffect(() => {
    setSelectedGroupIds(initialGroupIds);
    setCardId(mapping.cardId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapping.id]);

  const toggleGroup = (gid: string) => {
    setSelectedGroupIds((prev) =>
      prev.includes(gid) ? prev.filter((x) => x !== gid) : [...prev, gid],
    );
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between gap-4 space-y-0">
        <div className="space-y-1">
          <CardTitle className="flex items-center gap-2 text-base">
            <span dir="rtl">{mapping.orefTitle}</span>
            {!mapping.enabled && <Badge variant="secondary">disabled</Badge>}
            {mapping.enabled && !mapping.cardId && (
              <Badge variant="error">no card linked</Badge>
            )}
          </CardTitle>
        </div>
        <Switch
          checked={mapping.enabled}
          onCheckedChange={(v) => onPatch({ enabled: v })}
        />
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <div className="space-y-1 sm:col-span-2">
            <Label>Alert message</Label>
            <select
              className="w-full rounded border bg-background px-2 py-1 text-sm"
              value={cardId}
              onChange={(e) => {
                setCardId(e.target.value);
                onPatch({ cardId: e.target.value });
              }}
            >
              <option value="">— Pick a card —</option>
              {messages.map((mm) => (
                <option key={mm.id} value={mm.id}>
                  {mm.name} ({mm.id})
                </option>
              ))}
            </select>
          </div>
          <div className="flex items-end gap-3">
            <label className="flex items-center gap-2 text-sm">
              <Switch
                checked={mapping.sendHe}
                onCheckedChange={(v) => onPatch({ sendHe: v })}
              />
              HE
            </label>
            <label className="flex items-center gap-2 text-sm">
              <Switch
                checked={mapping.sendEn}
                onCheckedChange={(v) => onPatch({ sendEn: v })}
              />
              EN
            </label>
          </div>
        </div>

        <div className="space-y-2 border-t pt-4">
          <Label>Restrict to location groups</Label>
          <p className="text-xs text-muted-foreground">
            Empty = use the tenant's default subscription set. Picked = only
            fires when OREF reports locations inside the selected group(s).
          </p>
          <div className="flex flex-wrap gap-1">
            {groups.map((g) => {
              const selected = selectedGroupIds.includes(g.id);
              return (
                <Badge
                  key={g.id}
                  variant={selected ? "primary" : "secondary"}
                  className="cursor-pointer"
                  onClick={() => toggleGroup(g.id)}
                >
                  {g.name}
                  {g.status !== "active" && <span className="ml-1 opacity-50">(paused)</span>}
                </Badge>
              );
            })}
            {groups.length === 0 && (
              <span className="text-xs text-muted-foreground">No groups defined yet</span>
            )}
          </div>
          <Button
            size="sm"
            variant="outline"
            onClick={() => onSetAssignments(selectedGroupIds, initialCityValues)}
          >
            Save assignments
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
